/**
 * Bow and Arrow — public chat app shell (Task 201).
 *
 * This is a thin route switch. It resolves the language and the route, owns the
 * cross-route composer prefill (reader "ask about this" + shared `?q=` links) and
 * the About dialog ref, and delegates the real work:
 *   - /corpus  → <CorpusReader>;
 *   - else     → <ChatView>, which owns the chat lifecycle via useChat().
 *
 * The chat orchestration that used to live here (streaming reducer, answer
 * runners, morph state, persistence, citation highlight, every handler) now
 * lives in src/chat/* and src/hooks/use-language.ts. See
 * docs/public-chat-frontend-architecture-audit.md for the rationale.
 */
import { useCallback, useMemo, useRef } from "react";
import { AboutDialog } from "./components/AboutDialog";
import { ChatView } from "./chat/ChatView";
import { useChat } from "./chat/use-chat";
import { useComposerPrefill } from "./chat/use-composer-prefill";
import { ConnectionsRoute } from "./connections/ConnectionsRoute";
import { CaminosRoute } from "./caminos/CaminosRoute";
import { AdminRoute } from "./admin/AdminRoute";
import { CorpusReader } from "./corpus/CorpusReader";
import { useCorpusRoute, corpusPath, chatPath } from "./corpus/use-corpus-route";
import { useLanguage } from "./hooks/use-language";
import { useReducedMotion } from "./hooks/use-reduced-motion";
import { loadSession } from "./state/session-persistence";
import type { AnswerError } from "./api/answer-client";

export default function App(): JSX.Element {
  const reducedMotion = useReducedMotion();
  // Task 167: restore a prior in-tab conversation from sessionStorage. Read ONCE
  // (the store is not reactive); feeds the chat reducer + language initializers
  // so the app boots straight into the restored chat shell — no arrow replay.
  const persistedSession = useMemo(loadSession, []);
  const { language, setLanguage, strings, handleLanguageChange } = useLanguage(
    persistedSession?.language,
  );

  // Corpus Reader routing (Task 189). A minimal History-API route layer so
  // /corpus is deep-linkable; the chat is otherwise a boolean idle/chat view.
  const { route, navigate } = useCorpusRoute();
  const goCorpus = useCallback((): void => navigate(corpusPath()), [navigate]);
  // Deep-link into one book (the indexing notice's "newest book" link). Separate
  // from goCorpus on purpose: goCorpus is wired directly as an onClick handler in
  // places, so widening it to take a slug would let React hand it a MouseEvent.
  const goBook = useCallback(
    (book: string, chapter: number): void => navigate(corpusPath(book, chapter)),
    [navigate],
  );

  // Cross-route composer prefill (reader "ask about this" + shared `?q=` links).
  // Lives at the shell so the seed survives a /corpus → /chat navigation and can
  // be handed to <CorpusReader> as onAsk.
  const prefill = useComposerPrefill();
  const chat = useChat({ persistedSession, language, reducedMotion, prefill });

  const aboutRef = useRef<HTMLDialogElement>(null);
  const handleOpenAbout = useCallback((): void => {
    // Imperative open of the native modal dialog (Task 076). Handler-driven,
    // not Effect-driven; the <dialog> owns Escape, the focus trap, and
    // returning focus to the trigger on close.
    aboutRef.current?.showModal();
  }, []);

  // Task 191: reader "ask about this" seeds a fresh chat composer. Reset the
  // conversation (so the reader prompt starts as an unambiguous standalone — it
  // can't poison the follow-up cursor), seed the draft, then navigate to /chat.
  const handleAsk = useCallback(
    (prompt: string): void => {
      chat.resetForAsk();
      prefill.seedAsk(prompt);
      navigate(chatPath());
    },
    [chat, prefill, navigate],
  );

  // Task 216: the connections graph is a sibling view (lazy — the 3D bundle
  // loads only on /conexiones).
  if (route.view === "connections") {
    return (
      <div className="app app--corpus">
        <ConnectionsRoute
          route={route}
          navigate={navigate}
          language={language}
          onLanguage={setLanguage}
          strings={strings}
          onAsk={handleAsk}
          onAbout={handleOpenAbout}
        />
        <AboutDialog strings={strings} dialogRef={aboutRef} />
      </div>
    );
  }

  // Task 225: Caminos de Luz — guided study-flights over the affirmed graph
  // (lazy; shares the 3D chunk with /conexiones).
  if (route.view === "caminos") {
    return (
      <div className="app app--corpus">
        <CaminosRoute
          route={route}
          navigate={navigate}
          language={language}
          onLanguage={setLanguage}
          strings={strings}
          onAsk={handleAsk}
          onAbout={handleOpenAbout}
        />
        <AboutDialog strings={strings} dialogRef={aboutRef} />
      </div>
    );
  }

  // Post-release analytics-admin: the operator dashboard is a sibling view
  // (lazy — its chunk + admin.css load only on /admin). Spanish-only,
  // operator-only; NOT in any public nav. Auth is Caddy Basic Auth + the proxy
  // loopback bind, not the SPA.
  if (route.view === "admin") {
    return (
      <div className="app app--admin">
        <AdminRoute route={route} navigate={navigate} />
      </div>
    );
  }

  // Task 189: the Corpus Reader is a sibling view.
  if (route.view === "corpus") {
    return (
      <div className="app app--corpus">
        <CorpusReader
          route={route}
          navigate={navigate}
          language={language}
          onLanguage={setLanguage}
          strings={strings}
          onAsk={handleAsk}
          onAbout={handleOpenAbout}
        />
        <AboutDialog strings={strings} dialogRef={aboutRef} />
      </div>
    );
  }

  return (
    <ChatView
      chat={chat}
      prefill={prefill}
      strings={strings}
      language={language}
      onLanguageChange={handleLanguageChange}
      reducedMotion={reducedMotion}
      onAbout={handleOpenAbout}
      onCorpus={goCorpus}
      onOpenBook={goBook}
      aboutRef={aboutRef}
    />
  );
}

export type { AnswerError };
